import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  absoluteAsset,
  breadcrumbSchema,
  canonicalUrl,
  formatDescription,
  formatTitle,
  webPageSchema,
  type JsonLd,
} from "@/lib/seo";

/**
 * The static index.html head ships fallback tags for link-preview crawlers that
 * do not run JS. Once React is mounted, Helmet owns the head, so the static
 * duplicates are removed to avoid two descriptions/og:url values per page.
 */
const STATIC_DUPLICATES = [
  'meta[name="description"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'meta[property="og:url"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'link[rel="canonical"]',
];

const useStaticHeadCleanup = () => {
  useEffect(() => {
    for (const selector of STATIC_DUPLICATES) {
      document.head.querySelectorAll(selector).forEach((el) => {
        if (!el.hasAttribute("data-rh")) el.remove();
      });
    }
  }, []);
};


export interface SeoProps {
  /** Unique <title> for this page. */
  title: string;
  /** Unique meta description, ideally 120–158 characters. */
  description: string;
  /** Route path. Canonical and og:url are derived from it, query-free. */
  path: string;
  /** og:type — "website" by default, "article" for library pieces, "profile" for people. */
  ogType?: "website" | "article" | "profile";
  /** Sharing image; falls back to the sitewide 1200×630 card. */
  image?: string;
  /** Keep utility, internal, and duplicate-risk pages out of the index. */
  noindex?: boolean;
  /** Breadcrumb trail (without "Home" — it is prepended). */
  breadcrumbs?: { label: string; href?: string }[];
  /** Extra schema for this page: Article, FAQPage, VideoObject, Person, … */
  jsonLd?: JsonLd[];
  /** Set false on pages where a WebPage node adds nothing (e.g. the 404). */
  webPage?: boolean;
}

/**
 * Single source of head metadata for every public page: unique title and
 * description, self-referencing canonical, Open Graph and Twitter cards, index
 * control, and structured data (WebPage + BreadcrumbList by default).
 *
 * Note: these tags are applied client-side, so JS-executing crawlers such as
 * Googlebot read them; link-preview crawlers fall back to the static tags in
 * index.html.
 */
export const Seo = ({
  title: rawTitle,
  description: rawDescription,
  path,
  ogType = "website",
  image,
  noindex,
  breadcrumbs,
  jsonLd = [],
  webPage = true,
}: SeoProps) => {
  useStaticHeadCleanup();
  const pageTitle = formatTitle(rawTitle);
  const pageDescription = formatDescription(rawDescription);
  const url = canonicalUrl(path);
  const ogImage = image ? absoluteAsset(image) : DEFAULT_OG_IMAGE;
  const trail = breadcrumbs?.length ? [{ label: "Home", href: "/" }, ...breadcrumbs] : null;
  const breadcrumbNode = trail ? breadcrumbSchema(trail, url) : null;

  const nodes: JsonLd[] = [
    ...(webPage
      ? [
          webPageSchema({
            url,
            name: pageTitle,
            description: pageDescription,
            breadcrumbId: breadcrumbNode ? `${url}#breadcrumbs` : undefined,
          }),
        ]
      : []),
    ...(breadcrumbNode ? [breadcrumbNode] : []),
    ...jsonLd,
  ];

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large"} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />

      {nodes.map((node, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(node)}
        </script>
      ))}
    </Helmet>
  );
};
